// src/domain/constants.js
// Enums y constantes del dominio (CRM Los Toldos).
// Objetivo: unificar valores usados en UI y DB para evitar "strings mágicos".
// Nota: NO impongo cambios de esquema; dejo sets amplios y seguros.
// Cuando conectemos mappers/validators, afinamos según tu DB real.

export const APP = Object.freeze({
  NAME: "Los Toldos CRM",
  VERSION: "1.0.0",
});

// =========================
// Roles & Permissions
// =========================
export const ROLES = Object.freeze({
  ADMIN: "admin",
  MANAGER: "manager",
  VIEWER: "viewer",
});

export const ROLE_LABELS = Object.freeze({
  [ROLES.ADMIN]: "Administrador",
  [ROLES.MANAGER]: "Manager",
  [ROLES.VIEWER]: "Colaborador",
});


// Permisos por rol — viewer = músico contratado, puede ver pipeline y añadir gastos
export const ROLE_PERMISSIONS = Object.freeze({
  [ROLES.ADMIN]: {
    canEdit: true,
    canDelete: true,
    canManageUsers: true,
    canViewFinance: true,
    canViewAll: true,
    canAddExpenses: true,
    canEditProfile: true,
  },
  [ROLES.MANAGER]: {
    canEdit: true,
    canDelete: true,
    canManageUsers: false,
    canViewFinance: true,
    canViewAll: true,
    canAddExpenses: true,
    canEditProfile: true,
  },
  [ROLES.VIEWER]: {
    canEdit: false,
    canDelete: false,
    canManageUsers: false,
    canViewFinance: false,
    canViewAll: false,
    canAddExpenses: true,   // puede asociar gastos a eventos que atiende
    canEditProfile: true,   // puede editar su propio perfil
  },
});

// =========================
// Stages (nuevo pipeline comercial — 8 stages)
// =========================
// Proceso: lead → info_needed → proposal_sent → negotiation
//          → accepted_pending_deposit → confirmed → done | rechazado
export const STAGES = Object.freeze({
  LEAD: "lead",
  INFO_NEEDED: "info_needed",
  PROPOSAL: "proposal_sent",
  NEGOTIATION: "negotiation",
  ACCEPTED_PENDING_DEPOSIT: "accepted_pending_deposit",
  CONFIRMED: "confirmed",
  DONE: "done",
  LOST: "rechazado",
});

// Orden visual del funnel completo (incluye done para lista/calendario/filtros)
export const STAGE_ORDER = Object.freeze([
  STAGES.LEAD,
  STAGES.INFO_NEEDED,
  STAGES.PROPOSAL,
  STAGES.NEGOTIATION,
  STAGES.ACCEPTED_PENDING_DEPOSIT,
  STAGES.CONFIRMED,
  STAGES.DONE,
  STAGES.LOST,
]);

// Columnas del Kanban comercial (done no aparece como columna, rechazado sí)
export const KANBAN_STAGES = Object.freeze([
  STAGES.LEAD,
  STAGES.INFO_NEEDED,
  STAGES.PROPOSAL,
  STAGES.NEGOTIATION,
  STAGES.ACCEPTED_PENDING_DEPOSIT,
  STAGES.CONFIRMED,
  STAGES.LOST,
]);

export const STAGE_LABELS = Object.freeze({
  [STAGES.LEAD]: "Lead recibido",
  [STAGES.INFO_NEEDED]: "Info pendiente",
  [STAGES.PROPOSAL]: "Presupuesto enviado",
  [STAGES.NEGOTIATION]: "Dudas / negociación",
  [STAGES.ACCEPTED_PENDING_DEPOSIT]: "Aceptado – señal pendiente",
  [STAGES.CONFIRMED]: "Confirmado",
  [STAGES.DONE]: "Realizado",
  [STAGES.LOST]: "Rechazado / perdido",
});

// Stages que cuentan como ingresos seguros (reservados con señal)
export const STAGES_RESERVED = Object.freeze([STAGES.CONFIRMED, STAGES.DONE]);

// Stages "calientes" (leads con alta probabilidad de cerrar)
export const STAGES_HOT = Object.freeze([
  STAGES.PROPOSAL,
  STAGES.NEGOTIATION,
  STAGES.ACCEPTED_PENDING_DEPOSIT,
]);

// =========================
// Timeouts (Stagnant Leads Alert)
// =========================
// Días máximos sin cambio de stage antes de alertar
export const STAGE_TIMEOUTS = Object.freeze({
  [STAGES.LEAD]: 7,
  [STAGES.INFO_NEEDED]: 5,
  [STAGES.PROPOSAL]: 5,
  [STAGES.NEGOTIATION]: 7,
  [STAGES.ACCEPTED_PENDING_DEPOSIT]: 3, // prioridad alta: cobrar señal
});

// =========================
// Logistics Categories (para tareas en activities)
// =========================
export const LOGISTICS_CATEGORIES = Object.freeze({
  DOCUMENTATION: "documentation",
  LINEUP: "lineup",
  TECHNICAL: "technical",
  TRANSPORT: "transport",
  FINANCE: "finance",
  COMMUNICATION: "communication",
});

export const LOGISTICS_CATEGORY_LABELS = Object.freeze({
  [LOGISTICS_CATEGORIES.DOCUMENTATION]: "Documentación",
  [LOGISTICS_CATEGORIES.LINEUP]: "Lineup",
  [LOGISTICS_CATEGORIES.TECHNICAL]: "Técnica",
  [LOGISTICS_CATEGORIES.TRANSPORT]: "Transporte",
  [LOGISTICS_CATEGORIES.FINANCE]: "Finanzas",
  [LOGISTICS_CATEGORIES.COMMUNICATION]: "Comunicación",
});

// Plantillas de tareas logísticas iniciales (se crean al confirmar un evento)
export const LOGISTICS_TASK_TEMPLATES = Object.freeze([
  // --- Documentacion ---
  { category: "documentation", subject: "Cliente fiscal", description: "" },
  { category: "documentation", subject: "NIF/CIF", description: "" },
  { category: "documentation", subject: "Direccion fiscal", description: "" },
  { category: "documentation", subject: "Email facturacion", description: "" },
  { category: "documentation", subject: "Precio final", description: "" },
  { category: "documentation", subject: "Senal recibida", description: "" },
  { category: "documentation", subject: "Forma de pago", description: "" },
  { category: "documentation", subject: "Condiciones contrato", description: "" },

  // --- Tecnica ---
  // Tarima, luces y sonido se eligen en la tarjeta Proveedores (event_suppliers).
  { category: "technical", subject: "Furgoneta", description: "" },

  // --- Horarios ---
  { category: "transport", subject: "Hora de carga en el local", description: "" },
  { category: "transport", subject: "Llegada a la finca", description: "" },
  { category: "transport", subject: "Horario de montaje inicio", description: "" },
  { category: "transport", subject: "Horario de montaje fin maximo", description: "" },
  { category: "transport", subject: "Horario bolo", description: "" },
]);

// =========================
// Contact Types
// =========================
// Rol general del contacto en el CRM. Es independiente de si tambien es proveedor.
export const CONTACT_TYPES = Object.freeze({
  COUPLE: "couple",
  WEDDING_PLANNER: "wedding_planner",
  VENUE_MANAGER: "venue_manager",
  COMPANY: "company",
  OTHER: "other",
});

export const CONTACT_TYPE_LABELS = Object.freeze({
  [CONTACT_TYPES.COUPLE]: "Pareja",
  [CONTACT_TYPES.WEDDING_PLANNER]: "Wedding Planner",
  [CONTACT_TYPES.VENUE_MANAGER]: "Venue Manager",
  [CONTACT_TYPES.COMPANY]: "Empresa",
  [CONTACT_TYPES.OTHER]: "Otro",
});

export const CONTACT_TYPE_BADGE_CLASSES = Object.freeze({
  [CONTACT_TYPES.COUPLE]: "bg-pink-50 text-pink-700 border-pink-100",
  [CONTACT_TYPES.WEDDING_PLANNER]: "bg-amber-50 text-amber-700 border-amber-100",
  [CONTACT_TYPES.VENUE_MANAGER]: "bg-indigo-50 text-indigo-700 border-indigo-100",
  [CONTACT_TYPES.COMPANY]: "bg-emerald-50 text-emerald-700 border-emerald-100",
  [CONTACT_TYPES.OTHER]: "bg-slate-50 text-slate-700 border-slate-100",
});

export const CONTACT_TYPE_OPTIONS = Object.freeze([
  CONTACT_TYPES.COUPLE,
  CONTACT_TYPES.WEDDING_PLANNER,
  CONTACT_TYPES.VENUE_MANAGER,
  CONTACT_TYPES.COMPANY,
  CONTACT_TYPES.OTHER,
]);

// Rol dentro de un evento o relacion concreta (event_contacts.role_in_event).
export const CONTACT_ROLES = Object.freeze({
  MAIN: "main",
  SECONDARY: "secondary",
  BILLING: "billing",
  TECH: "tech",
  OTHER: "other",
});

export const CONTACT_ROLE_LABELS = Object.freeze({
  [CONTACT_ROLES.MAIN]: "Principal",
  [CONTACT_ROLES.SECONDARY]: "Secundario",
  [CONTACT_ROLES.BILLING]: "Facturacion",
  [CONTACT_ROLES.TECH]: "Tecnico",
  [CONTACT_ROLES.OTHER]: "Otro",
});

// =========================
// Supplier Types
// =========================
// Clasificacion adicional para contactos que tambien son proveedores.
export const SUPPLIER_TYPES = Object.freeze({
  VAN: "van",
  SOUND: "sound",
  STAGE: "stage",
  LIGHTING: "lighting",
  CATERING: "catering",
  HOTEL: "hotel",
  TRANSPORT: "transport",
  OTHER: "other",
});

export const SUPPLIER_TYPE_LABELS = Object.freeze({
  [SUPPLIER_TYPES.VAN]: "Transporte",
  [SUPPLIER_TYPES.SOUND]: "Sonido",
  [SUPPLIER_TYPES.STAGE]: "Tarima",
  [SUPPLIER_TYPES.LIGHTING]: "Iluminacion",
  [SUPPLIER_TYPES.CATERING]: "Catering",
  [SUPPLIER_TYPES.HOTEL]: "Hotel",
  [SUPPLIER_TYPES.TRANSPORT]: "Transporte",
  [SUPPLIER_TYPES.OTHER]: "Otro",
});

export const SUPPLIER_TYPE_OPTIONS = Object.freeze([
  SUPPLIER_TYPES.SOUND,
  SUPPLIER_TYPES.STAGE,
  SUPPLIER_TYPES.LIGHTING,
  SUPPLIER_TYPES.CATERING,
  SUPPLIER_TYPES.HOTEL,
  SUPPLIER_TYPES.TRANSPORT,
  SUPPLIER_TYPES.OTHER,
]);

// =========================
// Event Types
// =========================
// Set amplio: iremos ajustando a tus tipos reales
export const EVENT_TYPES = Object.freeze({
  CONCERT: "concert",
  FESTIVAL: "festival",
  PRIVATE: "private",
  CORPORATE: "corporate",
  WEDDING: "wedding",
  OTHER: "other",
});

export const EVENT_TYPE_LABELS = Object.freeze({
  [EVENT_TYPES.CONCERT]: "Concierto",
  [EVENT_TYPES.FESTIVAL]: "Festival",
  [EVENT_TYPES.PRIVATE]: "Privado",
  [EVENT_TYPES.CORPORATE]: "Corporativo",
  [EVENT_TYPES.WEDDING]: "Boda",
  [EVENT_TYPES.OTHER]: "Otro",
});

// =========================
// Channels (cómo entra el lead)
// =========================
export const CHANNELS = Object.freeze({
  INSTAGRAM: "instagram",
  WHATSAPP: "whatsapp",
  EMAIL: "email",
  PHONE: "phone",
  REFERRAL: "referral",
  WEB: "web",
  AGENCY: "agency",
  OTHER: "other",
});

export const CHANNEL_LABELS = Object.freeze({
  [CHANNELS.INSTAGRAM]: "Instagram",
  [CHANNELS.WHATSAPP]: "WhatsApp",
  [CHANNELS.EMAIL]: "Email",
  [CHANNELS.PHONE]: "Teléfono",
  [CHANNELS.REFERRAL]: "Recomendación",
  [CHANNELS.WEB]: "Web",
  [CHANNELS.AGENCY]: "Agencia",
  [CHANNELS.OTHER]: "Otro",
});

// =========================
// Regions (zonas / provincias / etc.)
// =========================
// Mantengo "ES" genérico + País Vasco por defecto, ajustable.
export const REGIONS = Object.freeze({
  BIZKAIA: "bizkaia",
  GIPUZKOA: "gipuzkoa",
  ARABA: "araba",
  NAVARRA: "navarra",
  CANTABRIA: "cantabria",
  BURGOS: "burgos",
  LA_RIOJA: "la_rioja",
  OTHER: "other",
});

export const REGION_LABELS = Object.freeze({
  [REGIONS.BIZKAIA]: "Bizkaia",
  [REGIONS.GIPUZKOA]: "Gipuzkoa",
  [REGIONS.ARABA]: "Araba",
  [REGIONS.NAVARRA]: "Navarra",
  [REGIONS.CANTABRIA]: "Cantabria",
  [REGIONS.BURGOS]: "Burgos",
  [REGIONS.LA_RIOJA]: "La Rioja",
  [REGIONS.OTHER]: "Otro",
});

// =========================
// Status (si en DB existe columna status separada de stage)
// =========================
// Lo dejamos opcional; mappers.js decidirá cómo mapear.
export const STATUSES = Object.freeze({
  OPEN: "open",
  CLOSED_WON: "closed_won",
  CLOSED_LOST: "closed_lost",
});

export const STATUS_LABELS = Object.freeze({
  [STATUSES.OPEN]: "Abierto",
  [STATUSES.CLOSED_LOST]: "Perdido",
});

// =========================
// Expense Owner (Personal vs Empresa)
// =========================
export const EXPENSE_OWNERS = Object.freeze({
  EMPRESA: "empresa",
  PERSONAL: "personal",
});

export const EXPENSE_OWNER_LABELS = Object.freeze({
  [EXPENSE_OWNERS.EMPRESA]: "Empresa",
  [EXPENSE_OWNERS.PERSONAL]: "Personal",
});

// =========================
// Expense Types / Categories
// =========================
export const EXPENSE_TYPES = Object.freeze({
  PERSONAL: "Personal",
  TRANSPORTE: "Transporte",
  ALOJAMIENTO: "Alojamiento",
  COMIDAS: "Comidas",
  OTROS: "Otros",
});
